/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';
import { kahuCopyInfoType } from 'types/kahu';

import { usePagination } from '@/hooks/usePagination';

import { KahuListPagerDock } from '../kahu-list-pager-dock';
import { PromptCard } from './prompt-card';
import {
  isPromptListIncomplete,
  parsePromptList,
  PromptBlock,
} from './prompt-list-utils';
import { PromptModal } from './prompt-modal';

const PROMPTS_PER_PAGE = 5;

export type PromptListLinkProps = {
  messageId: string;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: Dispatch<SetStateAction<kahuCopyInfoType | null>>;
};

export function PromptListSkeleton() {
  return (
    <div className="flex w-full flex-col gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex flex-col gap-2 rounded-lg border border-border bg-card-01 p-3"
        >
          <div className="h-3 w-32 animate-pulse rounded bg-card-02" />
          <div className="h-3 w-full animate-pulse rounded bg-card-02" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-card-02" />
        </div>
      ))}
    </div>
  );
}

export function PromptList({
  content,
  messageId,
  copiedInfo,
  setCopiedInfo,
  isStreaming = false,
}: { content: string; isStreaming?: boolean } & PromptListLinkProps) {
  const [selected, setSelected] = useState<{
    block: PromptBlock;
    index: number;
  } | null>(null);
  const blocks = useMemo(() => parsePromptList(content), [content]);
  const listRef = useRef<HTMLDivElement>(null);

  const {
    pageItems,
    page,
    setPage,
    totalPages,
    total,
    rangeStart,
    rangeEnd,
    showPager,
  } = usePagination(blocks, PROMPTS_PER_PAGE);

  if (isPromptListIncomplete(content) || blocks.length === 0) {
    return <PromptListSkeleton />;
  }

  // While streaming, the list is still growing, so pin to the first page and
  // hide the pager until the count is final.
  const pagerVisible = showPager && !isStreaming;

  return (
    <>
      <div ref={listRef} className="mt-3 flex w-full flex-col gap-3">
        {pageItems.map((block, i) => {
          const index = rangeStart - 1 + i;
          return (
            <PromptCard
              key={`${block.traceId}-${index}`}
              block={block}
              index={index}
              onOpen={() => setSelected({ block, index })}
              messageId={messageId}
              copiedInfo={copiedInfo}
              setCopiedInfo={setCopiedInfo}
            />
          );
        })}
      </div>
      {pagerVisible && (
        <KahuListPagerDock
          listRef={listRef}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          total={total}
          noun="prompts"
        />
      )}
      {selected && (
        <PromptModal
          block={selected.block}
          occurrenceKey={`${messageId}:prompt-modal:${selected.index}`}
          copiedInfo={copiedInfo}
          setCopiedInfo={setCopiedInfo}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
